// The end of the flow (Figma 24 / 25).
//
// This page must never claim success it cannot prove. On a refresh the store is
// empty, so it re-reads the submission: status "Sent" earns the success copy,
// anything else goes back to review. A page that says "sent!" because it was
// merely navigated to is the one bug here that would cost someone a role.

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import SubmissionOutcome, { OutcomePlan, OutcomeProOffer } from "../common/SubmissionOutcome";
import { proUpgradeUrl } from "../common/mediaLibrary";
import { goToDashboard } from "../common/dashboard";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { paysLast, peekStep4Variant } from "../common/step4Variant";
import { trackSubmissionEvent } from "../analytics";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

const SubmissionSentPage: React.FC<Props> = ({
  history,
  location,
  sent,
  review,
  membership,
  draft,
  loadReview,
}) => {
  const [checking, setChecking] = useState(!sent);
  const submissionId = draft ? draft.submissionId : 0;

  useEffect(() => {
    if (!isSubmissionAuthed()) {
      history.replace(`/submission/flow/verify-email${location.search}`);
      return;
    }
    if (sent) {
      trackSubmissionEvent("sf_step4_sent_page_viewed", {
        variant: peekStep4Variant(),
      });
      return;
    }
    // Refreshed, or arrived by URL. Ask the server what actually happened.
    if (!submissionId) {
      history.replace(`/submission/flow/step/4${location.search}`);
      return;
    }
    loadReview(submissionId).then((resp: any) => {
      setChecking(false);
      if (!resp || resp.status !== "Sent") {
        history.replace(`/submission/flow/step/4${location.search}`);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breakdownName = sent
    ? sent.breakdownName
    : review
    ? review.breakdownName
    : "";
  // Sent: "Explore more opportunities!" goes on to the classic site's breakdowns,
  // signed in as the dashboard link is (default.asp?tk=&cid=&page=breakdowns).
  const onExplore = () => {
    trackSubmissionEvent("sf_step4_explore_more", {});
    goToDashboard(history, "breakdowns");
  };
  // Not shown on the sent screen; the list works out the market from the IP.
  const onSeeCastingCalls = () => history.push("/submission/casting-calls");

  if (checking && !sent) {
    return (
      <SubmissionLayout>
        <Centered aria-busy="true">
          <Message>{i18next.t("One moment…")}</Message>
        </Centered>
      </SubmissionLayout>
    );
  }

  const onDashboard = () => {
    trackSubmissionEvent("sf_step4_to_dashboard", {});
    goToDashboard(history);
  };

  // Test A's payment is what sent it, so its success screen says the plan
  // started; test B's is about the submission only (docs/30 #20). The server
  // answers a plan only when the checkout started in the flow.
  const plan: OutcomePlan =
    sent && paysLast(peekStep4Variant()) && (sent.plan === "trial" || sent.plan === "pro")
      ? sent.plan
      : null;

  // Who submitted free gets Pro offered here (#23): after the submission went
  // out, never before it. Not who is already on a plan, which a free casting
  // call doesn't tell apart.
  const onPlan =
    !!membership &&
    (membership.membershipStatus === "trial" || membership.membershipStatus === "pro");
  const proOffer: OutcomeProOffer = onPlan
    ? null
    : (sent && sent.freeMarket) || (membership && membership.freeMarket)
    ? "us"
    : (sent && sent.freeCastingCall) || (review && review.freeCastingCall)
    ? "freeBreakdown"
    : null;

  return (
    <SubmissionLayout>
      <SubmissionOutcome
        kind="sent"
        plan={plan}
        trialEndsUtc={sent ? sent.planEndsUtc : null}
        proOffer={proOffer}
        breakdownName={breakdownName}
        onDashboard={onDashboard}
        onExplore={onExplore}
        onSeeCastingCalls={onSeeCastingCalls}
        onGoPro={() => {
          trackSubmissionEvent("sf_step4_sent_go_pro", { offer: proOffer });
          window.location.assign(proUpgradeUrl());
        }}
      />
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0 8px;
`;

const Message = styled.div`
  font-size: 14px;
  color: #53627c;
  line-height: 20px;
`;

interface StateProps {
  sent: iRootState["submissionStep4Model"]["sent"];
  review: iRootState["submissionStep4Model"]["review"];
  membership: iRootState["submissionStep4Model"]["membership"];
  draft: iRootState["submissionSignupModel"]["draft"];
}

interface DispatchProps {
  loadReview: (submissionId: number) => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  sent: state.submissionStep4Model.sent,
  review: state.submissionStep4Model.review,
  membership: state.submissionStep4Model.membership,
  draft: state.submissionSignupModel.draft,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  loadReview: dispatch.submissionStep4Model.loadReview,
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionSentPage);
